import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Autocomplete,
} from "@mui/material";
import { createUseStyles } from "react-jss";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { theme } from "$utils/theme/bataillog";

import { Text, Popin } from "$components";
import CField from "$forms/CField";
import { ProductReference } from "$types/ProductReference";
import { Reception } from "$types";
import JSelectDisplayOption from "$components/JSelectDisplayOption/JSelectDisplayOption";
import { useCommonStyles } from "$containers/CommonStyles/CommonStyles";
import ProductForm from "$containers/Products/ProductForm";
import { useProductReference } from "../ReferencingForm/hooks/useProductReference";

type Props = {
  companyId: string;
  hasReferencingCategory: boolean;
  isReadOnly: boolean;
  listIndex: number;
  nestIndex: number;
  productReference: ProductReference;
  reception: Reception;
  openDeleteModal: boolean;
  confirmedDeletions: boolean;
  setDeletedItems: React.Dispatch<React.SetStateAction<number[]>>;
};

function ProductReferenceItem({
  companyId,
  confirmedDeletions,
  hasReferencingCategory,
  isReadOnly,
  listIndex,
  nestIndex,
  productReference,
  reception,
  openDeleteModal,
  setDeletedItems,
}: Props) {
  const classes = useStyles();
  const commonClasses = useCommonStyles();

  const {
    fieldPath,
    control,
    errors,
    receivedQuantity,
    hasAtLeastOneReceived,
    infoMessage,
    stateReferenceUnique,
    isTouched,
    openProductForm,
    toggleOpenProductForm,
    prefilledReference,
    productError,
    setProductError,
    companyObject,
    onProductSubmit,
    copyReference,
    deleteNewReference,
    autocompleteProps,
  } = useProductReference({
    companyId,
    hasReferencingCategory,
    isReadOnly,
    listIndex,
    nestIndex,
    productReference,
    reception,
    openDeleteModal,
    confirmedDeletions,
    setDeletedItems,
  });

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.form}>
        <Box flex={5}>
          <CField
            disabled
            control={control}
            name={`${fieldPath}.originalProductReference`}
            label="Référence"
          />
        </Box>
        <Box flex={6}>
          <Box display="flex" alignItems="flex-end">
            <Box className={classes.autocompleteWrapper}>
              <Autocomplete
                {...autocompleteProps}
                className={`${commonClasses.select} ${classes.autocomplete}`}
                renderOption={(props, option) => (
                  <li {...props}>
                    <JSelectDisplayOption
                      value1={`${option.reference || option.name} 
                ${option.secondReference || ""}`}
                      value2={option.designation}
                    />
                  </li>
                )}
                getOptionLabel={(option: any) =>
                  option?.name || option?.reference || ""
                }
                disableClearable
                noOptionsText="Saisir une référence"
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    label="Nouvelle référence"
                    variant="outlined"
                    error={
                      !!(
                        errors.productReferenceLists &&
                        errors.productReferenceLists[listIndex]
                          ?.productReferences[nestIndex]?.productReference
                      )
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {hasAtLeastOneReceived(receivedQuantity) &&
                            hasReferencingCategory &&
                            !isReadOnly && (
                              <InputAdornment
                                position="end"
                                className={classes.newRefAdornment}
                              >
                                <IconButton
                                  className={classes.cancelBtn}
                                  onClick={deleteNewReference}
                                >
                                  <ClearRoundedIcon />
                                </IconButton>
                              </InputAdornment>
                            )}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
            {hasAtLeastOneReceived(receivedQuantity) && !isReadOnly && (
              <Button
                variant="outlined"
                onClick={copyReference}
                style={{
                  marginLeft: 4,
                  minWidth: 33,
                  height: 33,
                  padding: 0,
                  fontSize: "18px",
                }}
              >
                =
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      {!!infoMessage && <Text className={classes.infoText}>{infoMessage}</Text>}
      {!stateReferenceUnique && isTouched && (
        <Text className={classes.infoText}>
          Cette référence produit existe déjà. (Cat. référencement)
        </Text>
      )}

      <Popin
        openPopin={openProductForm}
        toggleDrawer={toggleOpenProductForm}
        customCloseAction={() => {
          toggleOpenProductForm(false);
          setProductError(undefined);
        }}
      >
        <ProductForm
          key={`product-form-${prefilledReference || "empty"}`}
          onSubmit={onProductSubmit}
          onReset={() => {
            toggleOpenProductForm(false);
            setProductError(undefined);
          }}
          company={companyObject}
          productError={productError}
          defaultValuesForm={{ reference: prefilledReference }}
        />
      </Popin>
    </Box>
  );
}

const useStyles = createUseStyles(() => ({
  autocompleteWrapper: {
    flex: 1,
    minWidth: 0,
  },
  autocomplete: {
    width: "100%",
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap",
    },
    "& .MuiAutocomplete-input": {
      minWidth: 0,
    },
  },
  cancelBtn: {
    padding: `0 !important`,
    "& svg": {
      width: "18px",
    },
  },
  newRefAdornment: {
    width: "fit-content !important",
  },
  form: {
    display: "flex",
    flexDirection: "row",
    gap: `${theme.spacing(2)}px`,
    minWidth: 0,
    "& > *": {
      minWidth: 0,
    },
  },
  infoText: {
    marginTop: `${theme.spacing(1)}px`,
    color: theme.color.blue,
    fontSize: "12px",
  },
  wrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
  },
}));

export default ProductReferenceItem;
